<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page customer-sales-detail-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="6" :sm="24">
              <a-form-item label="供应商">
                <a-select
                  v-model="query.supplierId"
                  allow-clear
                  show-search
                  option-filter-prop="children"
                  placeholder="全部供应商">
                  <a-select-option v-for="item in supplierOptions" :key="item.id" :value="item.id">
                    {{ item.supplierCode }} · {{ item.supplierName }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="8" :sm="24">
              <a-form-item label="日期范围">
                <a-range-picker v-model="dateRange" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="客商">
                <a-input v-model="query.customerKeyword" allow-clear placeholder="客商编号或名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="出库单号">
                <a-input v-model="query.outboundNo" allow-clear placeholder="销售出库单号" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>客商销售明细表</h3>
            <p>数据来源为已审核的正常销售出库单明细；供应商按商品档案 SKU 仓库配置（主/备选）筛选，不选则不过滤。</p>
          </div>
          <div class="erp-toolbar-actions">
            <a-button
              v-hasPermi="['erp:report:customer-sales-detail:export']"
              icon="download"
              :loading="exporting"
              :disabled="!pagination.total"
              @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          row-key="id"
          bordered
          size="middle"
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="{ x: 1680 }"
          :locale="{ emptyText: '暂无销售明细' }"
          @change="onPageChange">
          <template slot="outboundNo" slot-scope="text, record">
            <a @click="openOutboundDrawer(record)">{{ text }}</a>
          </template>
          <template slot="spec" slot-scope="text, record">{{ record.specDisplay || text || '—' }}</template>
          <template slot="qty" slot-scope="text, record">{{ record.qtyDisplay || text || '—' }}</template>
          <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
          <template slot="empty" slot-scope="value">{{ displayValue(value) }}</template>
        </a-table>
      </section>
    </div>

    <a-drawer
      :visible="outboundDrawerVisible"
      :width="960"
      :closable="false"
      :body-style="{ padding: 0 }"
      class="erp-doc-drawer"
      @close="closeOutboundDrawer">
      <div class="erp-drawer-shell">
        <header class="erp-drawer-header">
          <div>
            <button class="erp-back-button" type="button" aria-label="关闭" @click="closeOutboundDrawer">
              <a-icon type="arrow-left" />
            </button>
            <span class="erp-drawer-kicker">销售出库单</span>
            <h3>{{ outboundForm.outboundNo || '—' }}</h3>
          </div>
          <div><a-button @click="closeOutboundDrawer">关闭</a-button></div>
        </header>
        <div class="erp-drawer-content">
          <a-spin :spinning="outboundLoading">
            <section class="erp-form-card">
              <div class="erp-base-grid">
                <label><span>出库日期</span><a-input :value="outboundForm.outboundDate || '—'" disabled /></label>
                <label><span>审核状态</span><a-input :value="auditStatusText(outboundForm.auditStatus)" disabled /></label>
                <label><span>客商编码</span><a-input :value="outboundForm.customerCode || '—'" disabled /></label>
                <label><span>客商名称</span><a-input :value="outboundForm.customerName || '—'" disabled /></label>
                <label class="wide"><span>备注</span><a-input :value="outboundForm.remark || '—'" disabled /></label>
              </div>
            </section>
            <section class="erp-form-card">
              <div class="erp-section-heading-row"><div><h4>出库明细</h4></div></div>
              <a-table
                :columns="outboundLineColumns"
                :data-source="outboundForm.items || []"
                :pagination="false"
                row-key="id"
                size="small"
                bordered>
                <template slot="sku" slot-scope="text, row">{{ row.skuCode }} · {{ row.skuName }}</template>
                <template slot="qty" slot-scope="text, row">{{ formatLineQty(row) }}</template>
                <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
              </a-table>
            </section>
          </a-spin>
        </div>
      </div>
    </a-drawer>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import { getSalesOutbound } from '@/api/erp/salesOutbound'
import {
  pageCustomerSalesDetailReport,
  listCustomerSalesDetailSupplierOptions,
  exportCustomerSalesDetailReport
} from '@/api/erp/customerSalesDetailReport'

const emptyQuery = () => ({
  supplierId: undefined,
  customerKeyword: '',
  outboundNo: ''
})

export default {
  name: 'ErpCustomerSalesDetailReport',
  data () {
    return {
      query: emptyQuery(),
      dateRange: [moment().subtract(1, 'months'), moment()],
      supplierOptions: [],
      rows: [],
      loading: false,
      exporting: false,
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 条` },
      outboundDrawerVisible: false,
      outboundLoading: false,
      outboundForm: { items: [] },
      columns: [
        { title: '日期', dataIndex: 'outboundDate', width: 110 },
        { title: '出库单号', dataIndex: 'outboundNo', width: 190, scopedSlots: { customRender: 'outboundNo' } },
        { title: '客商编码', dataIndex: 'customerCode', width: 130, scopedSlots: { customRender: 'empty' } },
        { title: '客商名称', dataIndex: 'customerName', width: 160, scopedSlots: { customRender: 'empty' } },
        { title: '供应商', dataIndex: 'supplierName', width: 160, scopedSlots: { customRender: 'empty' } },
        { title: '规格', dataIndex: 'specDisplay', scopedSlots: { customRender: 'spec' }, width: 260 },
        { title: '销售单位', dataIndex: 'unitName', width: 96, scopedSlots: { customRender: 'empty' } },
        { title: '数量', dataIndex: 'qtyDisplay', width: 190, scopedSlots: { customRender: 'qty' } },
        { title: '销售单价', dataIndex: 'salePrice', width: 108, scopedSlots: { customRender: 'money' } },
        { title: '金额', dataIndex: 'amount', width: 108, scopedSlots: { customRender: 'money' } }
      ],
      outboundLineColumns: [
        { title: 'SKU', scopedSlots: { customRender: 'sku' }, width: 220 },
        { title: '单位', dataIndex: 'unitName', width: 80 },
        { title: '数量', scopedSlots: { customRender: 'qty' }, width: 120 },
        { title: '单价', dataIndex: 'salePrice', scopedSlots: { customRender: 'money' }, width: 100 },
        { title: '金额', dataIndex: 'amount', scopedSlots: { customRender: 'money' }, width: 100 }
      ]
    }
  },
  created () {
    this.loadSupplierOptions()
    this.load()
  },
  methods: {
    displayValue (value) {
      return value === null || value === undefined || value === '' ? '—' : value
    },
    formatMoney (value) {
      return Number(value || 0).toFixed(2)
    },
    auditStatusText (status) {
      return ({ unaudited: '未审核', audited: '已审核', reverse_audited: '已反审核' })[status] || status || '—'
    },
    formatLineQty (row) {
      const unit = row.unitName || ''
      const qty = row.qtyBusiness != null ? Number(row.qtyBusiness) : 0
      return `${qty} ${unit}`.trim()
    },
    queryParams () {
      return {
        ...this.query,
        dateStart: this.dateRange[0] ? this.dateRange[0].format('YYYY-MM-DD') : undefined,
        dateEnd: this.dateRange[1] ? this.dateRange[1].format('YYYY-MM-DD') : undefined,
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    loadSupplierOptions () {
      listCustomerSalesDetailSupplierOptions().then(res => {
        this.supplierOptions = res.data || []
      }).catch(() => { this.supplierOptions = [] })
    },
    load () {
      this.loading = true
      pageCustomerSalesDetailReport(this.queryParams()).then(res => {
        const data = res.data || {}
        this.rows = data.records || []
        this.pagination.total = Number(data.total || 0)
      }).finally(() => { this.loading = false })
    },
    search () {
      this.pagination.current = 1
      this.load()
    },
    resetQuery () {
      this.query = emptyQuery()
      this.dateRange = [moment().subtract(1, 'months'), moment()]
      this.search()
    },
    onPageChange (page) {
      this.pagination.current = page.current
      this.pagination.pageSize = page.pageSize
      this.load()
    },
    openOutboundDrawer (record) {
      if (!record || record.outboundId == null) return
      this.outboundDrawerVisible = true
      this.outboundLoading = true
      this.outboundForm = { items: [] }
      getSalesOutbound(record.outboundId).then(res => {
        this.outboundForm = res.data || { items: [] }
        this.outboundForm.items = this.outboundForm.items || []
      }).catch(err => {
        this.$message.error((err && err.message) || '加载出库单失败')
        this.closeOutboundDrawer()
      }).finally(() => { this.outboundLoading = false })
    },
    closeOutboundDrawer () {
      this.outboundDrawerVisible = false
      this.outboundForm = { items: [] }
    },
    async doExport () {
      this.exporting = true
      try {
        const params = { ...this.queryParams() }
        delete params.pageNum
        delete params.pageSize
        const res = await exportCustomerSalesDetailReport(params)
        const blob = res && res.data !== undefined ? res.data : res
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `客商销售明细_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        window.URL.revokeObjectURL(link.href)
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>
